/**
 * The runner behind the front screens and the loading scene: ONE small canvas drawn at art-pixel size and
 * scaled to the window by a whole number with nearest-neighbour (every pixel stays square at any window
 * size). What it draws is a *scene* (scenes/*.ts, one module each, loaded only when picked): a floating
 * island, a storm, a fortress, a roof under the moon, and so on, picked at random per entry (boot.ts).
 *
 * Cheap on purpose: each scene paints its sky, ridges and ground once per resize; a frame is some tens of
 * `drawImage` calls and a few hundred pixels of particles. The loop runs only while the front or the
 * loading scene is on screen. Under prefers-reduced-motion it paints one still.
 */
import { ahead } from '../render/rig/sets';
import type { SpriteBank, SpriteSet } from '../render/sprites';
import { artH, artW, artX, artY } from '../render/pixel';
import { viewOf } from '../render/sprites';
import { emoteFrame } from '../render/emotes';
import { SCENES, type SceneId } from './scenes';
import type { Theme, View } from './scenes/kit';
import { drawContactShadow } from '../render/contactShadow';
import { CONTACT, onArtGrid, stageRes } from './fig-grid';
import { bandAt, figureShadows, frontHour, paintHour, toneOn, type FigMark } from './hourTone';

/** Where the subject sits for a screen, as fractions of the canvas (the card's side is left free). */
export type Focus = 'title' | 'side' | 'center' | 'loading';
const FOCUS: Record<Focus, { x: number; y: number }> = {
  title: { x: 0.4, y: 0.64 },
  side: { x: 0.35, y: 0.64 },
  center: { x: 0.5, y: 0.64 },
  loading: { x: 0.5, y: 0.6 },
};

/** The two heroes: a man and a woman (looks in front/looks.ts). */
export interface Duo {
  m?: SpriteSet;
  f?: SpriteSet;
}

/** Asks for a figure's everyday frames ahead of need. */
export function warm(set: SpriteSet): void {
  ahead(() => {
    for (const v of [set, ...Object.values(set.views ?? {})]) {
      for (let i = 0; i < v.idle.length; i++) void v.idle[i];
      for (let i = 0; i < (v.ready?.length ?? 0); i++) void v.ready![i];
      for (let i = 0; i < (v.walk?.length ?? 0); i++) void v.walk[i];
      void v.atk;
    }
  });
}

export class Stage {
  g: CanvasRenderingContext2D;
  readonly main: CanvasRenderingContext2D;
  LW = 0;
  LH = 0;
  S = 2;
  /** Backing canvas pixels per art pixel: 2 puts figures on their own half-art-pixel grid (fig-grid.ts). */
  R = 1;
  t = 0;
  scene: SceneId | null = null;
  bank?: SpriteBank;
  duo: Duo = {};
  mx = 0;
  my = 0;
  /** Smoothed pointer, -1..1, for parallax. */
  sx = 0;
  sy = 0;
  readonly reduce = matchMedia('(prefers-reduced-motion: reduce)');
  cost = 0;
  frames = 0;
  private theme: Theme | null = null;
  private raf = 0;
  private last = 0;
  private running = false;
  private ready = false;
  private focus: Focus = 'title';
  private fx = FOCUS.title.x;
  private fy = FOCUS.title.y;
  private tmpC?: HTMLCanvasElement;
  /** At most this many frames a second (the lofi listen mode lowers it while the viewer is idle: front/lofi.ts). */
  fpsCap = 60;
  private drawnAt = 0;
  /** Called when a scene has been swapped in (boot.ts starts the matching music). */
  onScene?: (id: SceneId) => void;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.g = canvas.getContext('2d', { alpha: false })!;
    this.main = this.g;
    this.g.imageSmoothingEnabled = false;
  }

  init(): void {
    this.ready = true;
    this.resize();
  }

  setDuo(
    bank: SpriteBank,
    looks: { m: { sprite: string; outfit?: string[] }; f: { sprite: string; outfit?: string[] } },
  ): void {
    this.bank = bank;
    this.duo = { m: bank.of(looks.m), f: bank.of(looks.f) };
    for (const s of [this.duo.m, this.duo.f]) if (s) warm(s);
  }

  /** Another look's frames, asked for ahead (disciples, monks, guards). */
  looks(look: { sprite: string; outfit?: string[] }): SpriteSet | undefined {
    const set = this.bank?.of(look);
    // Scenes may ask every frame: warm each set once.
    if (set && !this.warmed.has(set)) {
      this.warmed.add(set);
      warm(set);
    }
    return set;
  }
  private readonly warmed = new WeakSet<SpriteSet>();

  setFocus(f: Focus): void {
    this.focus = f;
  }

  /** Swaps the picture in: loads that scene's module (and nothing else), builds it, draws one frame. */
  async setScene(id: SceneId): Promise<void> {
    const def = SCENES.find((s) => s.id === id) ?? SCENES[0]!;
    const mod = await def.load();
    this.scene = def.id;
    this.theme = mod.make(this);
    document.documentElement.dataset.scene = def.id;
    if (this.reduce.matches) this.t = this.theme.stillT;
    if (this.ready) {
      // A scene kept on the art grid (fig-grid.ts COARSE_SCENES) changes the backing: resize does it all.
      if (stageRes(this.S, def.id) !== this.R) this.resize();
      else {
        this.theme.resize();
        if (!this.running || this.reduce.matches) this.draw(0);
      }
    }
    this.onScene?.(def.id);
  }

  get stillT(): number {
    return this.theme?.stillT ?? 4;
  }

  /** Stills: stop and draw once at time `t` (seconds). */
  freeze(t: number): void {
    this.stop();
    this.t = t;
    this.draw(0);
  }

  /** The canvas size for the window: whole-number scale, art-pixel size (about 300–400 art pixels tall). */
  resize(): void {
    const W = Math.max(1, innerWidth);
    const H = Math.max(1, innerHeight);
    const S = Math.max(1, Math.min(Math.floor(H / 300), Math.floor(W / 420)));
    this.S = S;
    this.LW = Math.ceil(W / S);
    // A narrow (phone) screen: the picture fills the top; the form card covers the rest, so cap its height.
    const narrow = W < 760;
    this.LH = narrow ? Math.min(Math.ceil(H / S), Math.ceil(340 / S)) : Math.ceil(H / S);
    this.canvas.style.top = narrow ? '0' : '50%';
    this.canvas.style.transform = narrow ? 'translateX(-50%)' : 'translate(-50%, -50%)';
    const R = (this.R = stageRes(S, this.scene));
    this.canvas.width = this.LW * R;
    this.canvas.height = this.LH * R;
    this.canvas.style.width = `${this.LW * S}px`;
    this.canvas.style.height = `${this.LH * S}px`;
    // The scenes paint in art pixels; the backing is R times finer for the figures.
    this.main.setTransform(R, 0, 0, R, 0, 0);
    this.g = this.main;
    this.g.imageSmoothingEnabled = false;
    document.documentElement.style.setProperty('--px', `${S}px`);
    if (!this.ready || !this.theme) return;
    this.theme.resize();
    this.bgSet = false;
    if (this.reduce.matches || !this.running) this.draw(0);
  }

  pointer(nx: number, ny: number): void {
    this.mx = Math.max(-1, Math.min(1, nx));
    this.my = Math.max(-1, Math.min(1, ny));
  }

  start(): void {
    if (this.running || !this.ready) return;
    this.running = true;
    this.canvas.hidden = false;
    this.last = 0;
    const loop = (now: number) => {
      if (!this.running) return;
      // Fewer frames when capped (a calm picture left open costs little); a hidden tab gets no frames at all.
      if (this.fpsCap < 60 && this.drawnAt && now - this.drawnAt < 1000 / this.fpsCap - 2) {
        this.raf = requestAnimationFrame(loop);
        return;
      }
      this.drawnAt = now;
      const dt = this.last ? Math.min(0.1, (now - this.last) / 1000) : 0;
      this.last = now;
      this.t += dt;
      const t0 = performance.now();
      this.draw(dt);
      this.cost += (performance.now() - t0 - this.cost) * 0.05;
      this.frames++;
      if (this.reduce.matches) {
        this.running = false;
        return;
      }
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  /** A figure standing with its feet at (x, y): idle, ready stance, strike or walk frame; cloth in the wind; or a flat-colour silhouette. */
  fig(
    set: SpriteSet | undefined,
    heading: number,
    x: number,
    y: number,
    phase: number,
    pose: 'idle' | 'ready' | 'atk' | 'walk' | 'jump' = 'idle',
    wind?: { sway: number; flutter: number },
    dark?: string,
  ): void {
    if (!set) return;
    const { set: s, facing } = viewOf(set, heading, 1);
    let img: HTMLCanvasElement | undefined;
    if (pose === 'atk') img = s.atk;
    else if (pose === 'jump') img = s.jump;
    else {
      const frames =
        pose === 'ready' && s.ready?.length ? s.ready : pose === 'walk' && s.walk.length ? s.walk : s.idle;
      img = frames[Math.floor(this.t * 2.6 + phase) % frames.length];
    }
    if (!img) return;
    if (wind && s.cloth) {
      try {
        img = s.cloth(img, wind.sway, wind.flutter);
      } catch {
        // keep the plain frame
      }
    }
    if (pose !== 'jump') this.contact(s, facing, x, y);
    this.mark(set, s, x, y);
    this.blit(img, s, facing, x, y, dark);
  }

  /**
   * A figure at a life act (a townsfolk emote id: `life-zither`, `life-sweepYard`, render/lifeArt.ts) or any emote,
   * `t` seconds into its loop; falls back to standing when the figure has no such frames.
   */
  act(
    set: SpriteSet | undefined,
    heading: number,
    x: number,
    y: number,
    emote: string,
    t: number,
    wind?: { sway: number; flutter: number },
  ): void {
    if (!set) return;
    const { set: s, facing } = viewOf(set, heading, 1);
    const f = s.extra ? emoteFrame(emote, t) : null;
    let img: HTMLCanvasElement | undefined;
    try {
      img = f ? s.extra!(f[0], f[1]) : undefined;
    } catch {
      img = undefined;
    }
    img ??= s.idle[Math.floor(this.t * 2.6) % s.idle.length];
    if (!img) return;
    if (wind && s.cloth) {
      try {
        img = s.cloth(img, wind.sway, wind.flutter);
      } catch {
        // keep the plain frame
      }
    }
    this.contact(s, facing, x, y);
    this.mark(set, s, x, y);
    this.blit(img, s, facing, x, y);
  }

  /** The figure's shadows on the art grid, under the feet at (x, y): the hour's, as in play (front/hourTone.ts). */
  private contact(s: SpriteSet, facing: number, x: number, y: number): void {
    if (toneOn()) figureShadows(this.g, s, facing, x, y, this.scene);
    else drawContactShadow(this.g, x, y, CONTACT.rx, CONTACT.ry, CONTACT.alpha);
  }

  /** Figures drawn on the main canvas this frame (the night's light pools find the heroes by these). */
  private readonly figs: FigMark[] = [];
  private mark(set: SpriteSet, s: SpriteSet, x: number, y: number): void {
    if (this.g !== this.main || this.figs.length >= 64) return;
    this.figs.push({ x: Math.round(x), y: Math.round(y), h: s.h, hero: set === this.duo.m || set === this.duo.f });
  }

  private blit(
    frame: HTMLCanvasElement,
    s: SpriteSet,
    facing: number,
    x: number,
    y: number,
    dark?: string,
  ): void {
    const g = this.g;
    // On the 2× backing the frame lands 1:1 (G2); anywhere else (an art-res layer, a 1× stage) it goes to the art grid first.
    const img = g === this.main && this.R === 2 ? frame : onArtGrid(frame);
    g.save();
    g.translate(Math.round(x), Math.round(y));
    if (facing < 0) g.scale(-1, 1);
    if (dark) {
      // Tinted at the frame's own resolution, so a silhouette keeps its G2 edge.
      const w = img.width;
      const h = img.height;
      if (!this.tmpC) this.tmpC = document.createElement('canvas');
      if (this.tmpC.width < w) this.tmpC.width = w;
      if (this.tmpC.height < h) this.tmpC.height = h;
      const tg = this.tmpC.getContext('2d')!;
      tg.clearRect(0, 0, w, h);
      tg.drawImage(img, 0, 0);
      tg.globalCompositeOperation = 'source-atop';
      tg.fillStyle = dark;
      tg.fillRect(0, 0, w, h);
      tg.globalCompositeOperation = 'source-over';
      g.drawImage(this.tmpC, 0, 0, w, h, artX(img) - s.ax, artY(img) - s.ay, artW(img), artH(img));
    } else g.drawImage(img, artX(img) - s.ax, artY(img) - s.ay, artW(img), artH(img));
    g.restore();
  }

  private draw(dt: number): void {
    if (!this.theme || !this.ready) return;
    const { LW, LH } = this;
    const f = FOCUS[this.focus];
    const k = Math.min(1, dt * 3.2) || 1;
    this.fx += (f.x - this.fx) * k;
    this.fy += (f.y - this.fy) * k;
    this.sx += (this.mx - this.sx) * Math.min(1, dt * 3);
    this.sy += (this.my - this.sy) * Math.min(1, dt * 3);
    const narrow = innerWidth < 760;
    const loading = this.focus === 'loading';
    const v: View = {
      ox: Math.round(LW * (narrow || loading ? 0.5 : this.fx)),
      oy: Math.round(narrow ? LH * (loading ? 0.5 : 0.42) : LH * this.fy),
      safe: narrow || loading || this.focus === 'center' ? LW : Math.round(LW - 430 / this.S),
      sx: this.sx,
      sy: this.sy,
    };
    this.figs.length = 0;
    this.theme.draw(dt, v);
    // The hour over the picture, as in play (front/hourTone.ts); the UI reads the band for its contrast.
    paintHour(this.main, LW, LH, this.scene, this.t, v.ox, v.oy, this.figs);
    const band = toneOn() ? bandAt(frontHour()) : 'day';
    if (document.documentElement.dataset.hour !== band) document.documentElement.dataset.hour = band;
    if (narrow && !this.bgSet) {
      // Below the picture the page shows the colour of its bottom edge.
      const p = this.main.getImageData(Math.floor(LW / 2) * this.R, (LH - 1) * this.R, 1, 1).data;
      document.getElementById('ink')?.style.setProperty('background', `rgb(${p[0]},${p[1]},${p[2]})`);
      this.bgSet = true;
    }
  }
  private bgSet = false;
}
